import {ImportingDataTypes} from "../../../types";
import {SUPPORT_PREDEFINED_MULTISELECT_TECHNICAL_IMPORT} from "../../shared/import-feature-flags";
import {buildMappedPropertyFieldKeys} from "../../shared/technical-import-helpers";
import {resolvePredefinedPropertyValues} from "../../transformers/shared/resolve-predefined-property-values";
import {isPreventativeFieldSkippable, resolveFieldSkipTarget} from "./skip-field-cells";

/**
 * @param {*} property
 * @param {string} rawValue
 * @param {Record<string, Array<{id: *, title: string}>>} propertyOptionsByPropertyId
 */
const isTechnicalPropertyImportable = (property, rawValue, propertyOptionsByPropertyId) => {
    const trimmed = rawValue?.toString?.()?.trim?.() ?? "";
    if (!trimmed.length) {
        return false;
    }

    const hasPredefined = property?.config?.hasPreDefinedValues ?? property?.hasPreDefinedValues ?? false;
    const isMultiSelect = property?.config?.isMultiSelect ?? property?.isMultiSelect ?? false;

    if (hasPredefined && isMultiSelect && !SUPPORT_PREDEFINED_MULTISELECT_TECHNICAL_IMPORT) {
        return false;
    }

    if (hasPredefined) {
        const options = propertyOptionsByPropertyId[String(property.id)] ?? [];
        const resolution = resolvePredefinedPropertyValues(trimmed, options, {isMultiSelect});
        return resolution.resolved && resolution.apiValue !== undefined;
    }

    return true;
};

/**
 * Whether the field's current cell value would be imported if not skipped.
 * @param {Object} params
 * @param {string} params.fieldKey
 * @param {Object} params.parsedRow
 * @param {Array<*>} params.technicalProperties
 * @param {Record<string, Array<{id: *, title: string}>>} params.propertyOptionsByPropertyId
 */
export const isFieldImportableInCurrentRow = ({
    fieldKey,
    parsedRow,
    technicalProperties,
    propertyOptionsByPropertyId,
}) => {
    const propertyId = fieldKey.replace(/^property_/, '');
    const property = (technicalProperties ?? []).find(
        (entry) => String(entry.id) === propertyId,
    );
    if (!property) {
        return true;
    }

    const rawValue = parsedRow?.[fieldKey];
    return isTechnicalPropertyImportable(property, rawValue, propertyOptionsByPropertyId);
};

/**
 * @param {Object} params
 * @param {Object} params.parsedRow
 * @param {number} params.rowIndex
 * @param {string} params.fieldKeyToSkip
 * @param {Array<{rowIndex: number, fieldKey: string}>} params.skippedFieldCells
 * @param {Array<*>} params.technicalProperties
 * @param {Record<string, Array<{id: *, title: string}>>} params.propertyOptionsByPropertyId
 * @param {Set<string>} [params.mappedPropertyFieldKeys]
 */
export const wouldRowRetainImportableTechnicalProperty = ({
    parsedRow,
    rowIndex,
    fieldKeyToSkip,
    skippedFieldCells,
    technicalProperties,
    propertyOptionsByPropertyId,
    mappedPropertyFieldKeys,
}) => {
    const skippedSet = new Set(
        (skippedFieldCells ?? [])
            .filter((cell) => cell.rowIndex === rowIndex)
            .map((cell) => cell.fieldKey),
    );
    skippedSet.add(fieldKeyToSkip);

    const fieldKeys = mappedPropertyFieldKeys?.size
        ? [...mappedPropertyFieldKeys]
        : (technicalProperties ?? []).map((property) => `property_${property.id}`);

    for (const fieldKey of fieldKeys) {
        if (skippedSet.has(fieldKey)) {
            continue;
        }

        const propertyId = fieldKey.replace(/^property_/, '');
        const property = (technicalProperties ?? []).find(
            (entry) => String(entry.id) === propertyId,
        );
        if (!property) {
            continue;
        }

        const rawValue = parsedRow?.[fieldKey];
        if (isTechnicalPropertyImportable(property, rawValue, propertyOptionsByPropertyId)) {
            return true;
        }
    }

    return false;
};

/**
 * @param {Object} params
 * @param {{ id?: string }} params.selectedDataType
 * @param {import('./section-factories').PreventativeField} params.field
 * @param {number} params.rowIndex
 * @param {Object} params.parsedRow
 * @param {Array<{rowIndex: number, fieldKey: string}>} params.skippedFieldCells
 * @param {Object} [params.staticDataContext]
 * @param {Function} [params.t]
 */
export const evaluateFieldSkip = ({
    selectedDataType,
    field,
    rowIndex,
    parsedRow,
    skippedFieldCells,
    staticDataContext,
    t,
}) => {
    const issue = {rowIndex, originalRow: parsedRow};

    if (!isPreventativeFieldSkippable(field, issue)) {
        return {
            allowed: false,
            reason: t?.('skipFieldNotAllowed') ?? 'This field cannot be skipped.',
        };
    }

    const fieldKey = resolveFieldSkipTarget(field);
    const sourceId = selectedDataType?.id ?? "";

    if (sourceId === ImportingDataTypes.partTechnicalInformation) {
        const industryId = staticDataContext?.industryId;
        const technicalProperties = staticDataContext?.technicalPropertiesOfIndustry?.[industryId] ?? [];
        const propertyOptionsByPropertyId = staticDataContext?.propertyOptionsByPropertyId ?? {};
        const mappedPropertyFieldKeys = buildMappedPropertyFieldKeys(staticDataContext?.mappingFields);

        const fieldImportable = isFieldImportableInCurrentRow({
            fieldKey,
            parsedRow,
            technicalProperties,
            propertyOptionsByPropertyId,
        });

        if (!fieldImportable) {
            return {allowed: true};
        }

        const retainsImportable = wouldRowRetainImportableTechnicalProperty({
            parsedRow,
            rowIndex,
            fieldKeyToSkip: fieldKey,
            skippedFieldCells,
            technicalProperties,
            propertyOptionsByPropertyId,
            mappedPropertyFieldKeys,
        });

        if (!retainsImportable) {
            return {
                allowed: false,
                reason:
                    t?.('skipWouldLeaveNoImportableData') ??
                    'Skipping this would leave no importable technical properties for this row.',
            };
        }
    }

    return {allowed: true};
};
